/**
 * This module provides the BatterySettings higher order component.
 *
 * It defines the layout of the settings sections.
 *
 * And it prepares the consumed form values. The injected form values will be updated as soon as an update request was
 * initiated. This is required to trigger the reactivity behaviour of Formik later on.
 *
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { BatteryModel } from 'src/Libs/batteries'
import BatterySettingsForm from 'src/Elements/SystemSettings/Battery/settings/BatterySettingsForm'
import { SettingsFormValues } from 'src/Elements/SystemSettings/Battery/types'
import { useUserRoles } from 'src/Hooks/user'
import BatterySettingsList from 'src/Elements/SystemSettings/Battery/settings/BatterySettingsList'

import { FormikWithExitHandler } from 'src/Libs/form'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'
import { fromModelToFormModelTransformer } from 'src/Libs/batteries/transformers'
import { useBatterySettingsFormInitializer } from 'src/Libs/batteries/hooks'

interface Props {
  model: BatteryModel
  onUpdate: (values: SettingsFormValues) => Promise<void | boolean | null>
  isReadOnlyAccess?: boolean
}

const BatterySettingsData: React.FC<Props> = ({ model, onUpdate, isReadOnlyAccess }) => {
  const { isInstaller, isServiceUser } = useUserRoles()
  const { initialValues, validationSchema } = useBatterySettingsFormInitializer(model)
  const [formValues, setFormValues] = useState<SettingsFormValues>(initialValues)
  const { triggerAppRating } = useAppRatingContext()

  const hasWriteAccess = useMemo(() => !isReadOnlyAccess && (isInstaller || isServiceUser), [isInstaller, isServiceUser, isReadOnlyAccess])

  const handleUpdate = useCallback(
    async (values: SettingsFormValues) => {
      try {
        setFormValues(values)
        await onUpdate(values)
        triggerAppRating()
      } catch (error) {
        /* revert changes */
        setFormValues({ ...formValues })
      }
    },
    [onUpdate, formValues, triggerAppRating]
  )

  useEffect(() => {
    setFormValues(fromModelToFormModelTransformer(model))
  }, [model])

  const updateForm = (
    <FormikWithExitHandler
      formKey="battery-settings-data-form"
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={handleUpdate}
    >
      <BatterySettingsForm model={model} />
    </FormikWithExitHandler>
  )

  return (
    <>
      {hasWriteAccess && updateForm}
      {!hasWriteAccess && (
        <FormikWithExitHandler
          formKey="battery-settings-data-form"
          initialValues={formValues}
          validationSchema={validationSchema}
          onSubmit={handleUpdate}
        >
          <BatterySettingsList values={formValues} />
        </FormikWithExitHandler>
      )}
    </>
  )
}

export default BatterySettingsData
